import { isRecord, jsonNumber, jsonText } from '../json';
import { CHANNEL_KIND } from './catalog/channel';
import type { ChannelRef } from './catalog/channel';
import { isLoadIconKey } from './catalog/icon';
import { LOAD_TYPE_SPECS, isLoadTypeKey } from './catalog/loadType';
import { isRailTypeKey } from './catalog/railType';
import { SCHEMA_VERSION } from './catalog/storage';
import { DALI_BRIDGE_ADDRESS, DALI_MASTER } from './catalog/dali';
import { asDaliBridgeId, asLoadId, asRailId, asRoomId } from './ids';
import type { RoomId } from './ids';
import type { DaliBridge, DinRail, Load, RcuConfig, RcuIdentity, Room } from './model';

/**
 * Decodes stored JSON back into a configuration.
 *
 * This is the only place outside `ids.ts` that mints branded ids. Anything
 * malformed is dropped rather than guessed: a record that cannot be understood
 * disappears, and the caller is told how many did, instead of the app
 * half-believing a corrupt store.
 */
export interface DecodedConfig {
  readonly config: RcuConfig;
  readonly droppedRecords: number;
}

interface DecodedList<T> {
  readonly items: readonly T[];
  readonly dropped: number;
}

const decodeList = <T>(value: unknown, decode: (item: unknown) => T | null): DecodedList<T> => {
  const raw = Array.isArray(value) ? value : [];
  const decoded = raw.map((item: unknown) => decode(item));
  return {
    items: decoded.filter((item): item is T => item !== null),
    dropped: decoded.filter((item) => item === null).length,
  };
};

const decodeIdentity = (value: unknown): RcuIdentity | null => {
  if (!isRecord(value)) return null;
  const outputCount = jsonNumber(value.outputCount);
  if (outputCount === null) return null;
  return {
    name: jsonText(value.name),
    deviceId: jsonText(value.deviceId),
    // Absent in configurations stored before serial numbers were recorded.
    serialNumber: jsonText(value.serialNumber),
    ipAddress: jsonText(value.ipAddress),
    location: jsonText(value.location),
    outputCount,
  };
};

const decodeRail = (value: unknown): DinRail | null => {
  if (!isRecord(value)) return null;
  const id = jsonText(value.id);
  const type = jsonText(value.type);
  const address = jsonNumber(value.address);
  if (id === '' || !isRailTypeKey(type) || address === null) return null;
  return { id: asRailId(id), type, address, label: jsonText(value.label) };
};

/** A bridge whose address or bus is outside the 4-bit range is dropped. */
const decodeDaliBridge = (value: unknown): DaliBridge | null => {
  if (!isRecord(value)) return null;
  const id = jsonText(value.id);
  const address = jsonNumber(value.address);
  const master = jsonNumber(value.master);
  const inRange = (candidate: number, min: number, max: number): boolean =>
    candidate >= min && candidate <= max;
  if (id === '' || address === null || master === null) return null;
  if (!inRange(address, DALI_BRIDGE_ADDRESS.MIN, DALI_BRIDGE_ADDRESS.MAX)) return null;
  if (!inRange(master, DALI_MASTER.MIN, DALI_MASTER.MAX)) return null;
  return { id: asDaliBridgeId(id), address, master, label: jsonText(value.label) };
};

const decodeChannel = (value: unknown): ChannelRef | null => {
  if (!isRecord(value)) return null;
  const channel = jsonNumber(value.channel);
  if (channel === null) return null;
  if (value.kind === CHANNEL_KIND.RCU) return { kind: CHANNEL_KIND.RCU, channel };

  const railId = jsonText(value.railId);
  if (value.kind !== CHANNEL_KIND.DIN || railId === '') return null;
  return { kind: CHANNEL_KIND.DIN, railId: asRailId(railId), channel };
};

const decodeLoad = (value: unknown, fallbackCreatedAt: string): Load | null => {
  if (!isRecord(value)) return null;
  const id = jsonText(value.id);
  const type = jsonText(value.type);
  if (id === '' || !isLoadTypeKey(type)) return null;

  const roomId = jsonText(value.roomId);
  const iconKey = jsonText(value.iconKey);
  return {
    id: asLoadId(id),
    name: jsonText(value.name),
    type,
    roomId: roomId === '' ? null : asRoomId(roomId),
    // An unreadable channel becomes "unwired", which surfaces as a warning
    // rather than silently binding a load to the wrong output.
    channel: decodeChannel(value.channel),
    wattage: jsonNumber(value.wattage),
    iconKey: isLoadIconKey(iconKey) ? iconKey : LOAD_TYPE_SPECS[type].defaultIcon,
    createdAt: jsonText(value.createdAt) || fallbackCreatedAt,
  };
};

const decodeRoom = (value: unknown, fallbackCreatedAt: string): Room | null => {
  if (!isRecord(value)) return null;
  const id = jsonText(value.id);
  if (id === '') return null;
  return {
    id: asRoomId(id),
    name: jsonText(value.name),
    note: jsonText(value.note),
    createdAt: jsonText(value.createdAt) || fallbackCreatedAt,
  };
};

/** A default room is only kept when that room still exists. */
const existingRoomId = (rooms: readonly Room[], candidate: string): RoomId | null =>
  rooms.some((room) => room.id === candidate) ? asRoomId(candidate) : null;

/** Returns null when the payload is not a configuration this version understands. */
export const decodeConfig = (value: unknown, now: string): DecodedConfig | null => {
  if (!isRecord(value)) return null;
  if (value.schemaVersion !== SCHEMA_VERSION) return null;

  const rcu = decodeIdentity(value.rcu);
  if (rcu === null) return null;

  const updatedAt = jsonText(value.updatedAt) || now;
  const rails = decodeList(value.rails, decodeRail);
  // Absent in configurations stored before DALI bridges were modelled.
  const bridges = decodeList(value.daliBridges, decodeDaliBridge);
  const rooms = decodeList(value.rooms, (item) => decodeRoom(item, updatedAt));
  const loads = decodeList(value.loads, (item) => decodeLoad(item, updatedAt));

  return {
    config: {
      schemaVersion: SCHEMA_VERSION,
      rcu,
      rails: rails.items,
      daliBridges: bridges.items,
      loads: loads.items,
      rooms: rooms.items,
      defaultRoomId: existingRoomId(rooms.items, jsonText(value.defaultRoomId)),
      updatedAt,
    },
    droppedRecords: rails.dropped + bridges.dropped + loads.dropped + rooms.dropped,
  };
};
